/**
 * The "what are you asking about?" step shown before a WhatsApp chat opens.
 *
 * Shared by the inline WhatsApp buttons and the floating bubble so every
 * WhatsApp entry point behaves the same way. Questions are configured in the
 * CMS under Website settings; with none set, callers skip this entirely and
 * link straight to WhatsApp.
 */

import { X } from "lucide-react";
import { useSite, useWaLink } from "@/context/SiteDataContext";

/** Are there any preset questions configured? */
export function useWhatsAppPresets() {
  const site = useSite();
  return site?.whatsapp?.presetQuestions || [];
}

export default function WhatsAppChooser({ open, onClose, contextMessage }) {
  const presets = useWhatsAppPresets();
  const waLink = useWaLink();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Start a WhatsApp conversation"
        className="relative w-full max-w-md bg-white rounded-lg shadow-2xl overflow-hidden"
      >
        <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
          <div>
            <h3 className="font-bold text-ink">How can we help?</h3>
            <p className="mt-1 text-sm text-greyish">
              Choose a topic and we will pick up the conversation on WhatsApp.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-greyish hover:text-ink transition-colors shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <ul className="p-3 max-h-[60vh] overflow-y-auto">
          {/* A page-specific opener (e.g. "enquire about TALOS") leads, since it
              is almost always what the visitor actually wants. */}
          {contextMessage && (
            <li>
              <a
                href={waLink(contextMessage)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className="block px-4 py-3 mb-1 rounded-md text-sm font-semibold text-ink bg-[#f6f8f9] hover:bg-[#eef1f2] transition-colors"
              >
                {contextMessage}
              </a>
            </li>
          )}
          {presets.map((question) => (
            <li key={question}>
              <a
                href={waLink(question)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className="block px-4 py-3 rounded-md text-sm text-ink hover:bg-[#f6f8f9] transition-colors"
              >
                {question}
              </a>
            </li>
          ))}
          <li className="border-t border-border mt-2 pt-2">
            <a
              href={waLink("")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className="block px-4 py-3 rounded-md text-sm font-semibold text-[#1EBE5A] hover:bg-[#25D366]/10 transition-colors"
            >
              Something else — let me type my own message
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
}
